import { getDestinationGuide } from "@/lib/destination-guide.functions";
import { EXPLORA_DESTINATIONS } from "@/lib/explora-destinations";
import { SITE } from "@/lib/site";
import { SEARCH_COPY } from "@/lib/search-options";
import { Button } from "@/components/ui/button";
import { catalogBadges } from "@/lib/catalog-presentation";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { legacyTourAlias, legacyTourTarget } from "@/lib/legacy-redirects";
import { resolveLegacyTourSlug } from "@/lib/legacy-redirects.functions";
import { queryOptions, useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { MapPin, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n";

import { listUnifiedCatalog } from "@/lib/catalog.functions";
import {
  CITY_CATALOG,
  productsForCity,
  sortProducts,
  SORT_OPTIONS,
  type SortKey,
} from "@/lib/tour-search";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { UnifiedTourCard } from "@/components/site/UnifiedTourCard";

const productsQuery = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified", lang],
    queryFn: () => listUnifiedCatalog({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/tours/$city")({
  // Legacy WordPress product URLs used /tours/{productSlug}. Anything that is
  // not a curated city slug is resolved against the catalog (explicit alias
  // first) and permanently redirected instead of 404ing.
  beforeLoad: async ({ params, search }) => {
    if (CITY_CATALOG[params.city]) return;

    const alias = legacyTourAlias(params.city);
    if (alias) {
      throw redirect({ to: "/tour/$slug", params: { slug: alias }, search: search as never, statusCode: 301 });
    }

    let target = legacyTourTarget(params.city);
    try {
      target = await resolveLegacyTourSlug({ data: { slug: params.city } });
    } catch {
      // Keep the pure fallback target.
    }
    if (target.kind === "tour") {
      throw redirect({ to: "/tour/$slug", params: { slug: target.slug }, search: search as never, statusCode: 301 });
    }
    if (target.kind === "city") {
      throw redirect({ to: "/tours/$city", params: { city: target.city }, statusCode: 301 });
    }
    throw redirect({
      to: "/tours",
      search: target.q ? { q: target.q } : {},
      statusCode: 301,
    });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQuery(context.lang)),
  head: ({ params }) => {
    const c = CITY_CATALOG[params.city];
    if (!c) return { meta: [{ title: "Destino — Mostrando México" }] };
    const title = `Tours en ${c.name} — Mejores experiencias | Mostrando México`;
    const desc = `Reserva los mejores tours en ${c.name}, ${c.region}. ${c.tagline}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "place" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: `/tours/${params.city}` },
      ],
      links: [{ rel: "canonical", href: `https://www.mostrandomexico.mx/tours/${params.city}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristDestination",
            name: c.name,
            description: desc,
            containedInPlace: { "@type": "AdministrativeArea", name: c.region },
          }),
        },
      ],
    };
  },
  component: CityPage,
});

function CityPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Suspense fallback={<div className="h-96" />}>
        <CityBody />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function CityBody() {
  const { city } = Route.useParams();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const cfg = CITY_CATALOG[city];
  const { data } = useSuspenseQuery(productsQuery(lang));
  const [sort, setSort] = useState<SortKey>("featured");
  const destination = EXPLORA_DESTINATIONS.find(d => d.citySlug === city);
  const { data: existingGuide } = useQuery({
    queryKey: ["destination-guide", cfg.name, cfg.region, lang],
    queryFn: () => getDestinationGuide({ data: { title: cfg.name, location: `${cfg.name}, ${cfg.region}`, lang, generate: false } }),
    enabled: !!destination && productsForCity(data.tours, city).length === 0, staleTime: 3600_000,
  });

  const matches = useMemo(
    () => sortProducts(productsForCity(data.tours, city), sort),
    [data.tours, city, sort],
  );

  return (
    <>
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/8 via-surface to-accent/40">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,oklch(0.72_0.18_45/0.18),transparent_70%)]" />
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-11">
          <Link
            to="/tours"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> {t("catalogPage.allDestinations")}
          </Link>
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <MapPin className="h-3.5 w-3.5" /> {cfg.region}
          </div>
          <h1 className="mt-1.5 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {t("catalogPage.toursIn", { name: cfg.name })}
          </h1>
          <p className="mt-2 max-w-2xl text-base text-muted-foreground">
            {t(`destTagline.${city}`, { defaultValue: cfg.tagline })}
          </p>
          <p className="mt-1.5 text-sm font-semibold text-foreground/80">
            {matches.length}{" "}
            {matches.length === 1
              ? t("catalogPage.availableOne")
              : t("catalogPage.availableMany")}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-9 lg:px-8 lg:py-12">
        {matches.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <p className="font-display text-xl">
              {t("catalogPage.comingSoon", { name: cfg.name })}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("catalogPage.comingSoonDesc")}
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {destination && (existingGuide?.guide.sections.length ?? 0) > 0 && <Button asChild variant="outline"><Link to="/destino/$slug" params={{ slug: destination.slug }}>{SEARCH_COPY[lang].guide}</Link></Button>}
              <Button asChild variant="outline"><a href={`${SITE.contact.whatsappUrl}?text=${encodeURIComponent(cfg.name)}`} target="_blank" rel="noopener noreferrer">WhatsApp</a></Button>
            </div>
            <Link
              to="/tours"
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              {t("catalogPage.seeAllTours")}
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t("catalogPage.sortBy")}
              </p>
              <div className="hide-scrollbar inline-flex max-w-full overflow-x-auto rounded-full bg-muted p-1 text-xs font-semibold">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSort(opt.key)}
                    className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 transition ${
                      sort === opt.key
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t(`catalogPage.sort${opt.key.charAt(0).toUpperCase()}${opt.key.slice(1)}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {matches.map((p, i) => (
                <UnifiedTourCard key={`${p.source}-${p.id}`} tour={p} badge={catalogBadges(matches).get(p.id)} priority={i < 2} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}

/** Backwards-compat re-export. Prefer importing from "@/lib/tour-search". */
export { CITY_CATALOG };
